import { Component, inject } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { AuthService, Order, SelectedCustomerService, authTypes } from 'gung-standard';
import { Observable } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { OptionsList } from "gung-common";
import { TranslateService } from '@ngx-translate/core';
import {
  MOUNT_SEVERITY_MEDIUM,
  OttoOlsenClaimService,
  OttoClaimLine,
  OttoCreateClaimRequest
} from '../../services/otto-olsen-claim.service';


@Component({
  selector: 'otto-olsen-create-partial-order-claim',
  templateUrl: './otto-olsen-create-partial-order-claim.component.html',
  styleUrl: './otto-olsen-create-partial-order-claim.component.scss',
  standalone: false
})
export class OttoOlsenCreatePartialOrderClaimComponent {
  public form: FormGroup;
  public orderId: string;
  protected orderRowsActivated: any[] = [];
  protected images: File[] = [];
  protected currentUser: authTypes.User;
  protected customerNumber: string;
  protected customerName: string;
  public claimReasons: OptionsList[] = [];

  public loaded = false;

  private formBuilder = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private httpClient = inject(HttpClient);
  private claimService = inject(OttoOlsenClaimService);
  private authService = inject(AuthService);
  private selectedCustomerService = inject(SelectedCustomerService);
  protected router = inject(Router);
  private translate = inject(TranslateService);

  ngOnInit() {
    this.claimReasons = [
      { id: 'damaged', name: this.translate.instant('CLAIM_REASON_DAMAGED') },
      { id: 'missing', name: this.translate.instant('CLAIM_REASON_MISSING') }
    ];

    this.route.paramMap.subscribe(params => {
      this.orderId = params.get('orderId');
    });

    this.authService.getCurrentUser().pipe(filter(user => !!user), first())
      .subscribe(user => this.currentUser = user);

    this.selectedCustomerService.getSelectedCustomer().pipe(filter(customer => !!customer), first())
      .subscribe(customer => {
        this.customerNumber = customer?.id;
        this.customerName = customer?.name;
      });

    this.getOrder().subscribe((response) => {
        response.rows.forEach(row => {
          this.orderRowsActivated.push({...row, activated: false});
        });
        this.initForm();
        this.loaded = true;
    });
  }

  protected initForm() {
    const lineForms = Object.assign({}, ...this.orderRowsActivated.map(row => (
      {
        [`claim${this.removeDots(row.rownr + row.productId)}`]: this.formBuilder.control(false),
        [`quantityToClaim${this.removeDots(row.rownr + row.productId)}`]: this.formBuilder.control(null),
        [`claimReason${this.removeDots(row.rownr + row.productId)}`]: this.formBuilder.control(null)
      }
    )));
    this.form = this.formBuilder.group({
      ...lineForms,
      description: this.formBuilder.control(null),
      solution: this.formBuilder.control(null)
    });
  }

  protected getOrder(): Observable<Order> {
    const url = `json/orders/${this.orderId}`;
    return this.httpClient.get<Order>(url, {});
  }

  protected imagePreviews: { file: File; url: string }[] = [];

  protected addImages(event) {
    const files: FileList = event?.target?.files;
    if (!files?.length) {
      return;
    }
    const added = Array.from(files);
    this.images = [...this.images, ...added];
    this.imagePreviews = [
      ...this.imagePreviews,
      ...added.map(file => ({ file, url: URL.createObjectURL(file) }))
    ];
    event.target.value = '';
  }

  protected removeImage(index: number) {
    URL.revokeObjectURL(this.imagePreviews[index].url);
    this.imagePreviews.splice(index, 1);
    this.images.splice(index, 1);
  }

  ngOnDestroy() {
    this.imagePreviews.forEach(p => URL.revokeObjectURL(p.url));
  }

  protected cancel() {
    this.router.navigate(['/orders/', this.orderId]);
  }

  protected registerClaim() {
    const formRaw = this.form.getRawValue();
    const lines: OttoClaimLine[] = this.orderRowsActivated
      .filter(row => formRaw[`claim${this.removeDots(row.rownr + row.productId)}`])
      .map(row => {
        const key = this.removeDots(row.rownr + row.productId);
        return {
          lineNumber: row.rownr,
          articleNumber: row.productId,
          claimedQuantity: formRaw[`quantityToClaim${key}`],
          orderedQuantity: row.quantity,
          unit: row.unit,
          reason: formRaw[`claimReason${key}`]
        } as OttoClaimLine;
      });

    const claim: OttoCreateClaimRequest = {
      customerNumber: this.customerNumber,
      customerName: this.customerName,
      orderNumber: this.orderId,
      invoiceNumber: null,
      userName: this.currentUser?.name,
      userEmail: this.currentUser?.email,
      description: formRaw.description,
      primaryReason: null,
      desiredSolution: formRaw.solution,
      severityId: MOUNT_SEVERITY_MEDIUM,
      lines
    };

    this.claimService.createClaim(claim, this.images)
      .subscribe(() => this.router.navigate(['/otto-olsen-claim-receipt/', this.orderId]));
  }

  protected removeDots(productId: string): string {
    return productId.replace(/\./g, "");
  }
}
